import React from 'react';
import CTA from '../components/CTA';
import MiniContactForm from '../components/MiniContactForm';
import FAQ from '../components/FAQ';
import BlogTile from '../components/BlogTile';
import { BLOG_POSTS } from '../components/Blog';
import ServiceBlogSection from '../components/ServiceBlogSection';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';

const POOL_DECK_FAQ = [
  {
    question: 'How often should I have my pool deck cleaned?',
    answer: 'We recommend cleaning your pool deck at least once a year to prevent buildup of algae, mold, and dirt. In areas with heavy pool usage or high humidity, more frequent cleaning may be necessary.'
  },
  {
    question: 'Is your cleaning process safe for all pool deck materials?',
    answer: {
      intro: 'Yes, we use methods and solutions that are safe for all common pool deck materials:',
      list: [
        'Concrete pool decks',
        'Pavers and stone',
        'Wood decking',
        'Composite materials'
      ],
      outro: 'Our technicians will assess your specific surface and use the appropriate cleaning method.'
    }
  },
  {
    question: 'Can you remove stains and slippery spots from my pool deck?',
    answer: 'Absolutely! Our cleaning process is designed to remove stains and improve safety by eliminating slippery areas. We use specialized solutions to break down algae and other contaminants that can make surfaces dangerous.'
  }
];

const PoolDeckCleaning = () => {
  const serviceSEO = {
    title: 'Pool Deck Cleaning Services | Copper Canyon Pressure Wash',
    description: 'Professional pool deck cleaning in Phoenix, AZ. Remove slippery algae & stains. Expert technicians, eco-friendly solutions. Get your free quote today!'
  };
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Pool Deck Cleaning',
    'provider': {
      '@type': 'LocalBusiness',
      'name': 'Copper Canyon Pressure Wash'
    },
    'areaServed': {
      '@type': 'City',
      'name': ['Phoenix', 'Scottsdale', 'Mesa', 'Tempe', 'Gilbert', 'Paradise Valley']
    },
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Pressure Washing Services',
      'itemListElement': {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Pool Deck Cleaning'
        }
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO 
        title="Pool Deck Cleaning Services | Copper Canyon Pressure Wash"
        description="Professional pool deck cleaning in Phoenix, AZ. Remove slippery algae & stains. Expert technicians, eco-friendly solutions. Get your free quote today!"
        schema={serviceSchema}
        keywords="concrete pool deck cleaning, travertine pool deck cleaning, Phoenix pool deck maintenance, pool deck algae removal, pool deck safety cleaning, pool area maintenance, pool deck sealing preparation, pool deck restoration, pool deck slip prevention, pool deck surface cleaning"
      />
      <section className="w-full h-48 sm:h-64 md:h-80 lg:h-96 mb-8 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center" aria-label="Pool Deck Cleaning Banner">
        <picture>
          <source srcSet="/pool_deck_hero.avif" type="image/avif" />
          <source srcSet="/pool_deck_hero.webp" type="image/webp" />
        <img 
          src="/pool_deck_hero.png" 
          alt="Professional pool deck cleaning in action" 
          className="w-full h-full object-cover object-center" 
            width="1600"
            height="400"
        />
        </picture>
      </section>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold text-[#4a3f35] mb-6">Pool Deck Cleaning</h1>
        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4">Professional Pool Deck Cleaning Services</h2>
        <p className="mb-6 text-gray-700">
          At Copper Canyon Pressure Wash, we offer professional pool deck cleaning services for homeowners in the greater Phoenix area, working out of Scottsdale. Pool decks endure a lot—chlorine stains, algae growth, and slippery grime from constant water exposure can make them look worn and unsafe. Our team uses tailored pressure washing techniques to restore your pool deck's appearance and safety, ensuring it's a welcoming space for family and friends to enjoy.
        </p>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Slip-Resistant Clean</h3>
            </div>
            <p className="text-gray-700">We remove algae and mildew buildup that can make pool decks dangerously slippery, especially after Arizona's monsoon season.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Surface Brightening</h3>
            </div>
            <p className="text-gray-700">Our cleaning methods brighten faded surfaces, whether your pool deck is made of concrete, pavers, or natural stone, without causing wear.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Eco-Friendly Solutions</h3>
            </div>
            <p className="text-gray-700">We use eco-friendly detergents to ensure chlorine and grime are gone, while keeping your pool water and nearby plants safe.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Value Preservation</h3>
            </div>
            <p className="text-gray-700">Our thorough cleaning helps maintain your pool deck's value, making it look as inviting as the day it was installed.</p>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4 mt-8">Our Pool Deck Cleaning Process</h2>
        <div className="bg-[#f8f7f3] rounded-lg p-6 mb-8">
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">1</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Assessment</h3>
                <p className="text-gray-700">We begin by assessing your pool deck for algae, stains, or mineral deposits from pool water splashes.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">2</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Pool-Safe Pre-Treatment</h3>
                <p className="text-gray-700">We apply biodegradable, pool-safe detergents to break down grime and disinfect the surface without affecting your pool's chemistry.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">3</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Gentle or Strong Cleaning</h3>
                <p className="text-gray-700">Using adjustable pressure washers, we clean with a gentle setting for delicate materials like pavers, or a stronger setting for concrete, as needed.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">4</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Final Rinse & Inspection</h3>
                <p className="text-gray-700">We finish with a rinse and inspection, ensuring your pool deck is clean, safe, and ready for your next gathering.</p>
              </div>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4 mt-8">Our Expertise in Pool Deck Cleaning</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Pressure Adjustment</h3>
            <p className="text-gray-700">We adjust pressure settings—like using 1,200 PSI for pavers or 2,000 PSI for concrete—to avoid chipping or eroding your pool deck's surface.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Material Expertise</h3>
            <p className="text-gray-700">We know how to handle various pool deck materials, from textured concrete to travertine, ensuring each gets the right treatment.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Damage Prevention</h3>
            <p className="text-gray-700">Our expertise prevents issues like damaged grout or faded sealants, which can happen when inexperienced cleaners use the wrong techniques.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Pool Area Safety</h3>
            <p className="text-gray-700">We understand the unique demands of pool areas, ensuring a clean that's safe for both your deck and your pool environment.</p>
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto">
        <div className="bg-[#f8f7f3] border-l-4 border-[#b87c4c] p-6 rounded-lg shadow mb-8">
          <h3 className="text-lg font-bold text-[#b87c4c] mb-2">Get a Free Quote for Pool Deck Cleaning!</h3>
          <p className="mb-4 text-gray-700">Contact us today for a special offer on our pool deck cleaning services.</p>
          <Link 
            to="/contact" 
            className="inline-block px-6 py-2 bg-[#8B4513] text-white font-medium rounded-lg shadow hover:bg-[#6B3410] transition-colors duration-200"
          >
            Get a Free Quote
          </Link>
        </div>
      </div>
      <div className="max-w-2xl mx-auto mt-12 mb-12">
        <MiniContactForm />
      </div>
      <FAQ items={POOL_DECK_FAQ} title="Frequently Asked Questions About Pool Deck Cleaning" />

      {/* Blog Section */}
      <ServiceBlogSection blogSlugs={['safe-power-washing-pebbletec-pools']} />
    </div>
  );
};

export default PoolDeckCleaning; 